import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, Form, Button, Spinner, Alert, Badge, ListGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { apiClient } from '@/services/apiClient';
import { FaPencilAlt, FaEye, FaPlus, FaSave, FaArrowLeft, FaArrowRight, FaCheckDouble } from 'react-icons/fa';
import TemplatePreviewModal, { TemplatePreviewData } from '@/components/TemplatePreviewModal';
import './AssignReview.scss';

type AssignFieldType = 'text' | 'textarea' | 'select' | 'boolean' | 'number' | 'date';

interface AssignFormField {
  key: string;
  label: string;
  type: AssignFieldType;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  defaultValue?: string | number | boolean | null;
}

interface ReviewTemplateDto {
  templateGuid: string;
  templateName: string;
  category: string;
  description: string;
  isActive: boolean;
  reviewTemplateConfig?: string | null;
  totalSteps: number;
  requiredSteps: number;
}

interface WorkflowIndicators {
  escalatesTo?: string;  // followUpReviewName
  hasFinalReview: boolean;
}

function parseWorkflowIndicators(config?: string | null): WorkflowIndicators {
  if (!config) return { hasFinalReview: false };
  try {
    const parsed = JSON.parse(config) as Record<string, unknown>;
    const escalation = parsed?.escalation as Record<string, unknown> | undefined;
    const finalReview = parsed?.finalReview as Record<string, unknown> | undefined;
    return {
      escalatesTo: typeof escalation?.followUpReviewName === 'string' && escalation.followUpReviewName.trim()
        ? escalation.followUpReviewName.trim()
        : undefined,
      hasFinalReview: Boolean(
        (typeof finalReview?.assigneeEmail === 'string' && finalReview.assigneeEmail.trim()) ||
        (typeof finalReview?.role === 'string' && finalReview.role.trim())
      ),
    };
  } catch {
    return { hasFinalReview: false };
  }
}

const AssignReview: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<ReviewTemplateDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [selectedTemplateGuid, setSelectedTemplateGuid] = useState<string | null>(null);
  const [reviewName, setReviewName] = useState('');
  const [assignToEmail, setAssignToEmail] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignFields, setAssignFields] = useState<AssignFormField[]>([]);
  const [assignValues, setAssignValues] = useState<Record<string, string | number | boolean | null>>({});
  const [assignFormTitle, setAssignFormTitle] = useState('Template-Specific Fields');
  const [configWarning, setConfigWarning] = useState<string | null>(null);
  const [lastSuggestedName, setLastSuggestedName] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'disabled' | 'all'>('active');
  const [templateSearch, setTemplateSearch] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState<TemplatePreviewData | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const buildSuggestedName = (
    baseName: string,
    values: Record<string, string | number | boolean | null>
  ) => {
    const resourceType = String(values.resourceType ?? '').trim();
    const resourceId = String(values.resourceId ?? values.groupId ?? '').trim();
    const resourceName = String(values.resourceName ?? values.groupName ?? '').trim();

    if (!resourceType || !resourceId || !resourceType.toLowerCase().includes('group')) {
      return baseName;
    }

    const shortId = resourceId.length > 8 ? resourceId.slice(0, 8) : resourceId;
    return resourceName ? `${baseName} - ${resourceName} - ${shortId}` : `${baseName} - ${shortId}`;
  };

  const parseTemplateConfig = (rawConfig?: string | null): { title: string; fields: AssignFormField[]; warning: string | null } => {
    if (!rawConfig || !rawConfig.trim()) {
      return { title: 'Template-Specific Fields', fields: [], warning: null };
    }

    try {
      const parsed = JSON.parse(rawConfig) as {
        assignForm?: { title?: string; fields?: unknown[] };
      };

      const title = parsed.assignForm?.title?.trim() || 'Template-Specific Fields';
      const rawFields = Array.isArray(parsed.assignForm?.fields) ? parsed.assignForm?.fields ?? [] : [];

      const fields = rawFields
        .map<AssignFormField | null>((field) => {
          const f = field as Partial<AssignFormField>;
          const type = (f.type ?? 'text') as AssignFieldType;

          if (!f.key || !f.label) {
            return null;
          }

          if (!['text', 'textarea', 'select', 'boolean', 'number', 'date'].includes(type)) {
            return null;
          }

          return {
            key: String(f.key),
            label: String(f.label),
            type,
            required: Boolean(f.required),
            placeholder: f.placeholder ? String(f.placeholder) : undefined,
            options: Array.isArray(f.options) ? f.options.map((o) => String(o)) : undefined,
            defaultValue: f.defaultValue ?? null,
          };
        })
        .filter((f): f is AssignFormField => f !== null);

      return { title, fields, warning: null };
    } catch {
      return {
        title: 'Template-Specific Fields',
        fields: [],
        warning: 'Template config JSON is invalid. Continuing with the base assign form.'
      };
    }
  };

  useEffect(() => {
    const selected = templates.find((t) => t.templateGuid === selectedTemplateGuid);
    if (!selected) {
      setAssignFields([]);
      setAssignValues({});
      setAssignFormTitle('Template-Specific Fields');
      setConfigWarning(null);
      return;
    }

    const parsed = parseTemplateConfig(selected.reviewTemplateConfig);
    setAssignFields(parsed.fields);
    setAssignFormTitle(parsed.title);
    setConfigWarning(parsed.warning);

    const nextValues: Record<string, string | number | boolean | null> = {};
    parsed.fields.forEach((field) => {
      if (field.defaultValue !== undefined && field.defaultValue !== null) {
        nextValues[field.key] = field.defaultValue;
      } else if (field.type === 'boolean') {
        nextValues[field.key] = false;
      } else {
        nextValues[field.key] = '';
      }
    });
    setAssignValues(nextValues);

    const suggested = buildSuggestedName(selected.templateName.trim(), nextValues);
    if (!reviewName.trim() || reviewName === lastSuggestedName) {
      setReviewName(suggested);
    }
    setLastSuggestedName(suggested);
  }, [templates, selectedTemplateGuid]);

  useEffect(() => {
    const selected = templates.find((t) => t.templateGuid === selectedTemplateGuid);
    if (!selected) {
      return;
    }

    const suggested = buildSuggestedName(selected.templateName.trim(), assignValues);
    if (!reviewName.trim() || reviewName === lastSuggestedName) {
      setReviewName(suggested);
    }
    setLastSuggestedName(suggested);
  }, [assignValues, templates, selectedTemplateGuid, reviewName, lastSuggestedName]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<ReviewTemplateDto[]>('/api/admin/reviewmanagement/templates?includeInactive=true');
      setTemplates(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    let result = [...templates];

    if (statusFilter === 'active') {
      result = result.filter((t) => t.isActive);
    } else if (statusFilter === 'disabled') {
      result = result.filter((t) => !t.isActive);
    }

    const q = templateSearch.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (t) =>
          t.templateName.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.category?.toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => a.templateName.localeCompare(b.templateName));

    return result;
  }, [templates, statusFilter, templateSearch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTemplateGuid) {
      setError('Please select a template');
      return;
    }

    if (!reviewName.trim()) {
      setError('Please enter a review name');
      return;
    }

    if (!assignToEmail.trim()) {
      setError('Please enter the email address');
      return;
    }

    for (const field of assignFields) {
      if (!field.required) continue;
      const value = assignValues[field.key];
      const isMissing = value === null || value === undefined || String(value).trim() === '';
      if (isMissing) {
        setError(`${field.label} is required`);
        return;
      }
    }

    try {
      setSubmitting(true);
      const sessionGuid = localStorage.getItem('sessionGuid');
      const reviewConfig = assignFields.length > 0
        ? JSON.stringify(assignValues)
        : null;

      await apiClient.post('/api/admin/reviewmanagement/assign', {
        templateGuid: selectedTemplateGuid,
        reviewName: reviewName.trim(),
        assignToEmail: assignToEmail.trim(),
        dueDate: dueDate || null,
        reviewConfig,
        sessionGuid: sessionGuid
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/reviews');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to assign review');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreviewTemplate = (guid?: string) => {
    const targetGuid = guid ?? selectedTemplateGuid;
    const template = templates.find((t) => t.templateGuid === targetGuid);
    if (!template) return;

    // Select the template if it isn't already
    if (selectedTemplateGuid !== targetGuid) {
      setSelectedTemplateGuid(targetGuid);
    }

    setPreviewTemplate({
      templateName: template.templateName,
      templateCategory: template.category,
      templateDescription: template.description,
      totalSteps: template.totalSteps,
      requiredSteps: template.requiredSteps,
      steps: [],
      templateGuid: template.templateGuid,
      isLoadingSteps: true,
    });
    setShowPreview(true);

    // Load full template with steps
    apiClient
      .get(`/api/admin/reviewmanagement/templates/${template.templateGuid}`)
      .then((response) => {
        const fullTemplate = response.data;
        const stepsData = (fullTemplate.steps || []).map((step: any) => ({
          stepName: step.stepName || '(Untitled)',
          stepDescription: step.stepDescription || '',
          isRequired: step.isRequired,
          isActive: step.isActive,
        }));
        setPreviewTemplate((prev) =>
          prev ? { ...prev, steps: stepsData, isLoadingSteps: false } : null
        );
      })
      .catch(() => {
        setPreviewTemplate((prev) =>
          prev ? { ...prev, isLoadingSteps: false } : null
        );
      });
  };

  const handleCancelForm = () => {
    setSelectedTemplateGuid(null);
    setReviewName('');
    setAssignToEmail('');
    setDueDate('');
    setAssignFields([]);
    setAssignValues({});
    setError(null);
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">Loading templates...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
        <Button variant="warning" onClick={() => navigate('/admin/reviews')}>
          Back
        </Button>
      </Container>
    );
  }

  if (success) {
    return (
      <Container className="mt-4">
        <Alert variant="success">
          ✓ Review assigned successfully! Redirecting...
        </Alert>
      </Container>
    );
  }

  const selectedTemplate = templates.find(t => t.templateGuid === selectedTemplateGuid);

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h2 className="mb-0">Assign Review</h2>
        <Link to="/admin/reviews/templates/new">
          <Button variant="outline-primary" size="sm"><FaPlus className="me-1" />Add Review Template</Button>
        </Link>
      </div>
      <p className="text-muted">
        Assign a procedure, checklist, or testing template to a user
      </p>

      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center gap-2 mb-2 flex-wrap">
                <Form.Label className="mb-0">Review Template *</Form.Label>
                <div className="d-flex gap-2" style={{ minWidth: 0 }}>
                  <Form.Control
                    type="search"
                    size="sm"
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    aria-label="Search templates"
                    style={{ minWidth: 160 }}
                  />
                  <Form.Select
                    size="sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as 'active' | 'disabled' | 'all')}
                    aria-label="Filter templates by status"
                    style={{ minWidth: 160 }}
                  >
                    <option value="active">Active templates</option>
                    <option value="disabled">Disabled templates</option>
                    <option value="all">All templates</option>
                  </Form.Select>
                </div>
              </div>
              {templates.length === 0 ? (
                <Alert variant="info">No active templates available</Alert>
              ) : filteredTemplates.length === 0 ? (
                <Alert variant="info">
                  No templates match{templateSearch.trim() ? ` "${templateSearch.trim()}"` : ''}.
                </Alert>
              ) : (
                <ListGroup>
                  {filteredTemplates.map(template => (
                    <ListGroup.Item
                      as="div"
                      key={template.templateGuid}
                      active={selectedTemplateGuid === template.templateGuid}
                      onClick={() => {
                        if (template.isActive) {
                          setSelectedTemplateGuid(template.templateGuid);
                        }
                      }}
                      role="button"
                      tabIndex={template.isActive ? 0 : -1}
                      onKeyDown={(e) => {
                        if (template.isActive && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          setSelectedTemplateGuid(template.templateGuid);
                        }
                      }}
                      className={`review-template-item ${template.isActive ? 'cursor-pointer' : 'template-disabled'}`}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div>
                          <div className="fw-bold">{template.templateName}</div>
                          <div className={`small ${selectedTemplateGuid === template.templateGuid ? '' : 'text-muted'}`}>
                            {template.description}
                          </div>
                          {(() => {
                            const wf = parseWorkflowIndicators(template.reviewTemplateConfig);
                            if (!wf.escalatesTo && !wf.hasFinalReview) return null;
                            return (
                              <div className="d-flex flex-wrap gap-1 mt-1">
                                {wf.escalatesTo && (
                                  <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                    <FaArrowRight size={9} />
                                    Escalates to: {wf.escalatesTo}
                                  </span>
                                )}
                                {wf.hasFinalReview && (
                                  <span className="badge rounded-pill bg-warning-subtle text-warning-emphasis d-inline-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                                    <FaCheckDouble size={9} />
                                    Final review required
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          {!template.isActive && (
                            <div className="small text-warning-emphasis mt-1">
                              Disabled - enable it in Review Template Editor to assign again.
                            </div>
                          )}
                        </div>
                        <div className="text-end">
                          <Badge bg={template.isActive ? 'danger' : 'secondary'}>
                            {template.category}
                          </Badge>
                          <div className="small mt-1">
                            <Badge bg={template.isActive ? 'success' : 'secondary'}>
                              {template.isActive ? 'Active' : 'Disabled'}
                            </Badge>
                          </div>
                          <div className={`small mt-1 ${selectedTemplateGuid === template.templateGuid ? '' : 'text-muted'}`}>
                            {template.totalSteps} steps ({template.requiredSteps} required)
                          </div>
                          <div className="mt-2">
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-info"
                                size="sm"
                                className="p-1 d-inline-flex align-items-center justify-content-center"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePreviewTemplate(template.templateGuid);
                                }}
                                title="Preview template"
                                aria-label={`Preview template ${template.templateName}`}
                                style={{ position: 'relative', zIndex: 10 }}
                              >
                                <FaEye />
                              </Button>
                              <Link
                                to={`/admin/reviews/templates/${template.templateGuid}/edit`}
                                onClick={(e) => e.stopPropagation()}
                                className="btn btn-outline-secondary btn-sm p-1 d-inline-flex align-items-center justify-content-center"
                                title="Edit template"
                                aria-label={`Edit template ${template.templateName}`}
                                style={{ position: 'relative', zIndex: 10 }}
                              >
                                <FaPencilAlt />
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Form.Group>

            {selectedTemplate && !selectedTemplate.isActive && (
              <Alert variant="warning" className="mb-4">
                This template is disabled and cannot be assigned until it is re-enabled in the template editor.
              </Alert>
            )}

            {selectedTemplate && selectedTemplate.isActive && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Review Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={reviewName}
                    onChange={(e) => setReviewName(e.target.value)}
                    placeholder="e.g., Phase 1 Testing - January 2026"
                    disabled={submitting}
                    required
                  />
                  <Form.Text className="text-muted">
                    Give this review instance a descriptive name
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Assign To (Email) *</Form.Label>
                  <Form.Control
                    type="email"
                    value={assignToEmail}
                    onChange={(e) => setAssignToEmail(e.target.value)}
                    placeholder="user@enmax.com"
                    disabled={submitting}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Due Date (Optional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    disabled={submitting}
                  />
                </Form.Group>

                {configWarning && (
                  <Alert variant="warning" className="mb-3">
                    {configWarning}
                  </Alert>
                )}

                {assignFields.length > 0 && (
                  <Card className="mb-3 border-light-subtle">
                    <Card.Header>
                      <strong>{assignFormTitle}</strong>
                    </Card.Header>
                    <Card.Body>
                      {assignFields.map((field) => {
                        const value = assignValues[field.key];
                        const controlId = `assign-field-${field.key}`;

                        if (field.type === 'boolean') {
                          return (
                            <Form.Group className="mb-3" key={field.key} controlId={controlId}>
                              <Form.Check
                                type="switch"
                                label={`${field.label}${field.required ? ' *' : ''}`}
                                checked={Boolean(value)}
                                onChange={(e) => setAssignValues((cur) => ({ ...cur, [field.key]: e.target.checked }))}
                                disabled={submitting}
                              />
                            </Form.Group>
                          );
                        }

                        if (field.type === 'select') {
                          return (
                            <Form.Group className="mb-3" key={field.key} controlId={controlId}>
                              <Form.Label>{field.label}{field.required ? ' *' : ''}</Form.Label>
                              <Form.Select
                                value={String(value ?? '')}
                                onChange={(e) => setAssignValues((cur) => ({ ...cur, [field.key]: e.target.value }))}
                                disabled={submitting}
                              >
                                <option value="">Select...</option>
                                {(field.options ?? []).map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          );
                        }

                        if (field.type === 'textarea') {
                          return (
                            <Form.Group className="mb-3" key={field.key} controlId={controlId}>
                              <Form.Label>{field.label}{field.required ? ' *' : ''}</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={String(value ?? '')}
                                placeholder={field.placeholder}
                                onChange={(e) => setAssignValues((cur) => ({ ...cur, [field.key]: e.target.value }))}
                                disabled={submitting}
                              />
                            </Form.Group>
                          );
                        }

                        return (
                          <Form.Group className="mb-3" key={field.key} controlId={controlId}>
                            <Form.Label>{field.label}{field.required ? ' *' : ''}</Form.Label>
                            <Form.Control
                              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                              value={String(value ?? '')}
                              placeholder={field.placeholder}
                              onChange={(e) => {
                                const nextValue = field.type === 'number'
                                  ? (e.target.value === '' ? '' : Number(e.target.value))
                                  : e.target.value;
                                setAssignValues((cur) => ({ ...cur, [field.key]: nextValue }));
                              }}
                              disabled={submitting}
                            />
                          </Form.Group>
                        );
                      })}
                    </Card.Body>
                  </Card>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    variant="warning"
                    onClick={handleCancelForm}
                    disabled={submitting}
                  >
                    <FaArrowLeft className="me-1" />
                    Cancel
                  </Button>
                  <Button type="submit" variant="success" disabled={submitting}>
                    <FaSave className="me-1" />
                    {submitting ? 'Assigning...' : 'Assign Review'}
                  </Button>
                </div>
              </>
            )}
          </Form>
        </Card.Body>
      </Card>

      {/* Template Preview Modal */}
      <TemplatePreviewModal 
        show={showPreview} 
        onHide={() => setShowPreview(false)}
        template={previewTemplate}
        showEditorLink
      />
    </Container>
  );
};

export default AssignReview;
